import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, Zap, Crown, ArrowRight, Loader2, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 49,
    icon: Zap,
    highlight: false,
    features: [
      "5 workflows actifs",
      "1 000 exécutions / mois",
      "Email + alertes basiques",
      "Support email FR",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 149,
    icon: MessageCircle,
    highlight: true,
    badge: "Le + populaire",
    features: [
      "25 workflows actifs",
      "10 000 exécutions / mois",
      "WhatsApp Business inclus",
      "Marketplace & Copilot IA",
      "Support prioritaire (4h)",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: 399,
    icon: Crown,
    highlight: false,
    features: [
      "Workflows illimités",
      "100 000 exécutions / mois",
      "SSO + RBAC multi-workspace",
      "DPA signé, audit logs",
      "Support dédié (1h, slack)",
    ],
  },
];

/**
 * Full-screen modal shown when the 14-day trial has expired.
 * Blocks dashboard access until user picks a plan or logouts.
 */
export default function TrialExpiredModal() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [extending, setExtending] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    if (!user?.trial_ends_at) return;
    const end = new Date(user.trial_ends_at).getTime();
    const now = Date.now();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    setDaysLeft(diff);
  }, [user?.trial_ends_at]);

  // Compute trial state
  const isActiveSubscriber = user?.subscription_status === "active";
  const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at).getTime() : null;
  const isExpired = !isActiveSubscriber && trialEnd && trialEnd < Date.now();

  const subscribe = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Erreur lors de la création du paiement");
      setLoadingPlan(null);
    }
  };

  const requestExtension = async () => {
    setExtending(true);
    try {
      await api.post("/trial/request-extension");
      toast.success("7 jours supplémentaires ajoutés. Notre équipe va vous contacter.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de prolonger l'essai pour le moment.");
    } finally {
      setExtending(false);
    }
  };

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  if (!isExpired) return null;

  const alreadyExtended = !!user?.trial_extended;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md flex items-start md:items-center justify-center overflow-y-auto py-8 px-4"
      data-testid="trial-expired-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative max-w-5xl w-full bg-white rounded-sm shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-black text-white px-8 py-8 grain relative">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute -right-24 -top-24 w-72 h-72 rounded-full opacity-25 pointer-events-none"
            style={{ background: "conic-gradient(from 0deg, #0038FF, transparent, #0038FF)" }}
          />
          <div className="relative">
            <p className="font-mono text-xs tracking-widest text-zinc-400 mb-3">
              ESSAI TERMINÉ {daysLeft !== null ? `· EXPIRÉ DEPUIS ${Math.abs(daysLeft)} JOUR${Math.abs(daysLeft) > 1 ? "S" : ""}` : ""}
            </p>
            <h2 className="font-display text-3xl lg:text-4xl font-medium tracking-tighter mb-3">
              Votre essai gratuit est terminé.
            </h2>
            <p className="text-zinc-300 max-w-2xl">
              Vos workflows sont sauvegardés. Choisissez le plan adapté à votre PME pour réactiver
              l'exécution automatique et l'accès complet à votre tableau de bord.
            </p>
          </div>
        </div>

        {/* Plans */}
        <div className="px-6 md:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.id}
                  className={`relative border rounded-sm p-6 flex flex-col ${
                    p.highlight ? "border-black ring-1 ring-black bg-[#FAFAFA]" : "border-zinc-200"
                  }`}
                  data-testid={`trial-expired-plan-${p.id}`}
                >
                  {p.badge && (
                    <span className="absolute -top-3 left-6 klein-bg text-white text-[10px] font-mono tracking-widest uppercase px-2 py-1 rounded-sm">
                      {p.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${p.highlight ? "klein-bg text-white" : "bg-zinc-100 text-zinc-700"}`}>
                      <Icon size={16} />
                    </div>
                    <span className="font-display text-lg font-medium tracking-tight">{p.name}</span>
                  </div>
                  <div className="mb-5">
                    <span className="font-display text-4xl font-medium tracking-tighter">{p.price}€</span>
                    <span className="text-sm text-zinc-500"> /mois HT</span>
                  </div>
                  <ul className="space-y-2 text-sm mb-6 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-zinc-700">
                        <Check size={14} className="klein-text mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => subscribe(p.id)}
                    disabled={loadingPlan === p.id}
                    className={`w-full rounded-sm h-11 ${
                      p.highlight
                        ? "klein-bg text-white hover:bg-blue-700"
                        : "bg-black text-white hover:bg-zinc-800"
                    }`}
                    data-testid={`trial-expired-cta-${p.id}`}
                  >
                    {loadingPlan === p.id ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <ArrowRight size={16} className="mr-2" />
                    )}
                    Choisir {p.name}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 pt-6 border-t border-zinc-200">
            {/* 4th CTA - request extension */}
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-amber-500 text-white flex items-center justify-center shrink-0">
                <Calendar size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-base font-medium tracking-tight">
                  Pas encore prêt à choisir ?
                </p>
                <p className="text-sm text-zinc-600">
                  {alreadyExtended
                    ? "Vous avez déjà bénéficié d'une extension de 7 jours."
                    : "Nous vous offrons 7 jours supplémentaires pour finaliser votre évaluation. Notre équipe vous contactera pour comprendre vos besoins."}
                </p>
              </div>
              <Button
                onClick={requestExtension}
                disabled={extending || alreadyExtended}
                variant="outline"
                className="rounded-sm border-amber-400 text-amber-900 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed h-10 shrink-0"
                data-testid="trial-expired-extension-cta"
              >
                {extending ? (
                  <Loader2 size={14} className="mr-2 animate-spin" />
                ) : (
                  <Calendar size={14} className="mr-2" />
                )}
                {alreadyExtended ? "Déjà utilisée" : "Demander 7 jours"}
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
              <p>
                💳 Paiement sécurisé via Stripe · TVA française appliquée · Résiliable à tout moment
              </p>
              <button
                onClick={onLogout}
                className="text-zinc-600 hover:text-black underline"
                data-testid="trial-expired-logout"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
